<template>
    <div class="container">
        <div class="title-container">
            <span>
                {{ $t('facture.title') }} {{ this.numeroFacture }}
                <span class="regles-pastilles-group">
                    <ReglePastille cle="facturesFournisseur.definition" />
                    <ReglePastille cle="factureFournisseurDetail.historiqueRejets" />
                    <ReglePastille cle="factureFournisseurDetail.reglementsListe" />
                </span>
            </span>
        </div>
        <BorderedIconButton :imgUrl="imgUrl" :onClick="this.$router.back" className="back-btn" title="RETOUR" />
        <span class="bouton-avec-regle btn-contacter">
            <BorderedIconButton
                :imgUrl="imgContactUrl"
                :onClick="contacter"
                :title="$t('facture.fournisseur.contacter.bouton')"
            />
            <ReglePastille cle="factureFournisseurDetail.contacterRecipient" />
        </span>
        <StatusFournisseurTag v-if="status !== null" :status="this.status" />
        <DataList
            v-if="this.factureData && this.isLoaded"
            :data="this.factureData"
            :stop-propa-on-pdf="true"
            @select="goToPage"
        />
        <WaitingModal v-if="isAttachmentLoading" />
        <Modal v-if="modalContacterOpen">
            <ModalCard
                :activeValidate="isFormValid"
                :headerTitle="$t('contact.page.title')"
                @cancel="annuler"
                @confirm="envoyer"
            >
                <div class="form-group">
                    <TextInput v-model="objet" class="input-objet" label="Objet" />
                    <AreaText id="sujetTextarea" v-model="sujet" class="areaText" label="Sujet" />
                </div>
                <p class="info-text">
                    {{
                        isFactureRejetee
                            ? $t('facture.fournisseur.modal.comptabilite')
                            : $t('facture.fournisseur.modal.assistante') + ' - '
                    }}{{ nom.toUpperCase() }}
                    {{
                        prenom
                            .split(' ')
                            .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
                            .join(' ')
                    }}
                </p></ModalCard
            >
        </Modal>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import DataList from '@/components/ui-components/DataList.vue';
import ErrorManager from '@/common/utils/ErrorManager';
import { ERROR } from '@/modules/ErrorToaster/store/mutation-types';
import { store } from '@/plugins/store';
import WaitingModal from '@/components/ui-components/WaitingModal.vue';
import router from '@/router';
import FactureService, { FilterFacturesFournisseur } from '@/modules/FacturesFournisseurs/services/FactureService';
import { ReglementDTO } from '@/models/ReglementDTO';
import StatusFournisseurTag, { statusFournisseurName } from '@/components/ui-components/StatusTagFournisseur.vue';
import { MENU } from '@/components/layout/store/mutation-types';
import { MENU_FOURNISSEUR, MENU_FOURNISSEUR_SWITCH_CLIENT } from '@/components/layout/store/state';
import dayjs from 'dayjs';
import BorderedIconButton from '@/components/ui-components/BorderedIconButton.vue';
import { FactureFournisseurActions } from '@/modules/FacturesFournisseurs/store/action-types';
import { FactureFournisseurDTO } from '@/models/FactureFournisseurDTO';
import { SortType } from '@/models/SortType';
import ModalCard from '@/components/ui-components/ModalCard.vue';
import Modal from '@/components/ui-components/Modal.vue';
import TextInput from '@/components/ui-components/TextInput.vue';
import AreaText from '@/components/ui-components/AreaText.vue';
import ReglePastille from '@/components/regles/ReglePastille.vue';

type DataType = {
    factureData: Array<any>;
    status: string | null;
    isLoaded: boolean;
    numeroFacture: string;
    modalContacterOpen: boolean;
    objet: string;
    sujet: string;
    nom: string;
    prenom: string;
    isFactureRejetee: boolean;
};

export default defineComponent({
    name: 'FactureFournisseurPage',
    components: {
        AreaText,
        TextInput,
        Modal,
        ModalCard,
        StatusFournisseurTag,
        DataList,
        BorderedIconButton,
        WaitingModal,
        ReglePastille,
    },
    data(): DataType {
        return {
            factureData: [],
            isLoaded: false,
            status: null,
            numeroFacture: '',
            modalContacterOpen: false,
            objet: '',
            sujet: '',
            nom: '',
            prenom: '',
            isFactureRejetee: false,
        };
    },
    async beforeMount() {
        if (localStorage.getItem('isFournisseur') === 'true' && localStorage.getItem('isClient') === 'true') {
            store.commit(`menuState/${MENU.SET_MENU}`, MENU_FOURNISSEUR_SWITCH_CLIENT);
        } else {
            store.commit(`menuState/${MENU.SET_MENU}`, MENU_FOURNISSEUR);
        }

        let factureData: any;
        let historiqueFacture: any[] = [];

        if (this.$route.query.isFactureRejetee === 'true') {
            this.isFactureRejetee = true;
            const id = Array.isArray(this.$route.params.id) ? this.$route.params.id[0] : this.$route.params.id;

            await FactureService.getFactureFournisseurRejeteeById(id)
                .then((res: any) => {
                    factureData = res.data;
                })
                .catch(async (err: any) => {
                    const message = await ErrorManager.processError(err.response);

                    this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                    this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
                });
        } else {
            await FactureService.getFactureFournisseurById(+this.$route.params.id)
                .then((res: any) => {
                    factureData = res.data;
                })
                .catch(async (err: any) => {
                    const message = await ErrorManager.processError(err.response);

                    this.$store.commit(`errorToaster/${ERROR.SET_ACTIVE}`, true);
                    this.$store.commit(`errorToaster/${ERROR.SET_MESSAGE}`, message);
                });
        }
        const filtreHistoriqueFacture: FilterFacturesFournisseur = { numero: factureData.numero, isHistorique: true };
        await FactureService.getFacturesFournisseur(filtreHistoriqueFacture).then((factureData: any) => {
            historiqueFacture = factureData.data.content;
        });
        const responsable = factureData.responsableAdministratif;
        this.nom = responsable?.nom ?? '';
        this.prenom = responsable?.prenom ?? '';
        this.status = statusFournisseurName(factureData.statut);
        this.factureData = [
            { label: this.$t('facture.fournisseur.detail.societe'), value: factureData.societe },
            { label: this.$t('facture.fournisseur.detail.nom.fournisseur'), value: factureData.fournisseur },
            { label: this.$t('facture.fournisseur.detail.numero.facture'), value: factureData.numero },
            {
                label: this.$t('facture.fournisseur.detail.date'),
                value: dayjs(factureData.date).format('DD/MM/YYYY'),
            },
            {
                label: this.$t('facture.fournisseur.detail.numero.commande'),
                value: factureData.numeroCommande,
            },
            {
                label: this.$t('facture.fournisseur.detail.montant'),
                value: factureData.montantTTC + ' ' + this.$t('devise.' + factureData.devise),
            },
            { label: this.$t('facture.fournisseur.detail.type'), value: factureData.typeFacture },
            { label: this.$t('facture.fournisseur.detail.chantiers'), value: factureData.numeroBudget },
            {
                label: this.$t('facture.fournisseur.detail.echeance'),
                value: factureData.dateEcheance == null ? '' : dayjs(factureData.dateEcheance).format('DD/MM/YYYY'),
            },
            { label: this.$t('facture.fournisseur.detail.commentaires'), value: factureData.commentaires },
            { label: this.$t('facture.fournisseur.detail.cause.rejet'), value: factureData.causeRejet },
            { label: this.$t('facture.fournisseur.detail.cause.litige'), value: factureData.causeLitige },
        ];

        this.numeroFacture = factureData.numero;

        if (factureData.reglements != null) {
            const tableHeader = [
                { label: this.$t('facture.fournisseur.detail.table.reglements.numero') },
                { label: this.$t('facture.fournisseur.detail.table.reglements.date') },
                { label: this.$t('facture.fournisseur.detail.table.reglements.montant') },
            ];

            const tableData = factureData.reglements.map(
                ({ id, numero, dateReglement, montantTTC, devise }: ReglementDTO) => ({
                    id: { pageName: 'reglementFournisseurPage', value: id, label: numero },
                    date: dayjs(dateReglement).format('DD/MM/YYYY') ?? '',
                    montant:
                        montantTTC == undefined
                            ? 0 + ' ' + this.$t('devise.' + devise)
                            : montantTTC + ' ' + this.$t('devise.' + devise),
                })
            );

            const noDataText = this.$t('facture.fournisseur.no.reglements');

            this.factureData.push({
                label: this.$t('facture.fournisseur.detail.reglements'),
                value: { tableHeader, tableData, noDataText },
            });
        }

        if (historiqueFacture != null) {
            const tableHeader = [
                { label: this.$t('facture.fournisseur.detail.table.historique.date'), sortStatus: SortType.INACTIVE },
                { label: this.$t('facture.fournisseur.detail.table.historique.cause'), sortStatus: SortType.INACTIVE },
                {
                    label: this.$t('facture.fournisseur.detail.table.historique.commentaire'),
                    sortStatus: SortType.INACTIVE,
                },
                { label: this.$t('facture.fournisseur.detail.table.historique.status'), sortStatus: SortType.INACTIVE },
            ];

            const tableData = historiqueFacture.map(
                ({ date, causeRejet, commentaires, statut }: FactureFournisseurDTO) => {
                    return {
                        date: dayjs(date).format('DD/MM/YYYY') ?? '',
                        cause: causeRejet,
                        commentaire: commentaires,
                        statutFournisseur: statusFournisseurName(statut),
                    };
                }
            );

            const noDataText = this.$t('facture.fournisseur.no.historique');

            this.factureData.push({
                label: this.$t('facture.fournisseur.historique'),
                value: { tableHeader, tableData, noDataText },
            });
        }

        this.isLoaded = true;
    },
    computed: {
        imgUrl() {
            return require('@/assets/img/chevron-left-white.svg');
        },
        imgContactUrl() {
            return require('@/assets/img/menu/contactez-nous 1.png');
        },
        attachment() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsAttachment'];
        },
        isAttachmentLoading() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsIsAttachmentLoading'];
        },
        contentType() {
            return store.getters['suiviDemandeInterventionModuleState/getDemandesInterventionsAttachmentType'];
        },
        isFormValid(): boolean {
            return this.objet.trim() !== '' && this.sujet.trim() !== '';
        },
    },
    methods: {
        goToPage(item: { id: { pageName: string; value: number } }) {
            router.push({ name: item.id.pageName, params: { id: item.id.value } });
        },
        resetForm() {
            this.objet = '';
            this.sujet = '';
        },
        contacter() {
            this.modalContacterOpen = true;
            this.resetForm();
        },
        annuler() {
            this.modalContacterOpen = false;
            this.resetForm();
        },
        envoyer() {
            const payload = {
                id: this.$route.params.id,
                objet: this.objet,
                sujet: this.sujet,
                isFactureRejetee: this.isFactureRejetee,
            };
            this.$store.dispatch(`factureFournisseurState/${FactureFournisseurActions.CONTACTER}`, payload);
            this.modalContacterOpen = false;
            this.resetForm();
        },
    },
});
</script>

<style scoped>
.container {
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 24px;
}

.title-container {
    color: var(--color-principal);
    font-size: var(--font-size-large);
    font-weight: var(--font-weight-600);
    margin-bottom: 15px;
}

.regles-pastilles-group {
    display: inline-flex;
    align-items: center;
    gap: 16px;
}

.btn-contacter {
    position: absolute;
    right: 150px;
}
.bouton-avec-regle {
    display: inline-block;
    position: absolute;
}
.bouton-avec-regle .regle-pastille {
    position: absolute;
    top: -6px;
    right: -6px;
}

@media (max-width: 1600px) {
    .container-modale {
        height: 35vh;
        width: 37vw;
    }
}

@media (max-height: 650px) {
    :deep(textarea) {
        max-height: 20vh;
    }
}

@media (max-width: 1500px) {
    :deep(textarea) {
        max-width: 35vw;
    }
}
.form-group {
    display: flex;
    flex-direction: column;
    gap: 20px;
    width: 632px;
}
.info-text {
    font-style: italic;
    margin-top: 12px;
    font-size: var(--font-size-very-small);
    color: var(--grey-menu);
}
.input-objet {
    max-width: 600px;
    width: 100%;
}

.areaText {
    max-width: 600px;
}
</style>
